import * as React from 'react';
import {useEffect, useMemo, useState} from 'react';
import Link from '@mui/material/Link';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import {Chip, Stack, ToggleButton, ToggleButtonGroup, Typography} from '@mui/material';
import {Link as RouterLink} from 'react-router-dom';
import {Check} from '@mui/icons-material';
import merchants from '../../models/merchants';
import helpers from '../../helpers/common';
import useRootHook from '../../hooks/useRoot';
import TableToolbar from '../layout/TableToolbar';

const hasPaymentsAccess = (row) => row?.can_create_payments === true || row?.can_create_payments === 1;

const parseDailyLimit = (row) => {
    const v = row?.max_daily_volume != null ? parseFloat(row.max_daily_volume) : null;
    if (v == null || Number.isNaN(v) || v <= 0) return null;
    return v;
};

const formatAmount = (amount, symbol) => {
    const formatted = amount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    return symbol ? `${formatted} ${symbol}` : formatted;
};

const formatDailyLimit = (row, symbol) => {
    const v = parseDailyLimit(row);
    if (v == null) return 'Unlimited';
    return formatAmount(v, symbol);
};

const formatRemaining = (row, symbol) => {
    const limit = parseDailyLimit(row);
    if (limit == null) return '—';
    if (row.remaining != null && !Number.isNaN(parseFloat(row.remaining))) {
        return formatAmount(parseFloat(row.remaining), symbol);
    }
    const today = parseFloat(row.day_total || 0) || 0;
    return formatAmount(Math.max(0, limit - today), symbol);
};

export default function MerchantPaymentsAccessTable() {
    const {setShowLoader, defaultCurrency} = useRootHook();
    const [data, setData] = useState(null);
    const [view, setView] = useState('on');
    const currencySymbol = defaultCurrency?.symbol || defaultCurrency?.currency || '';

    useEffect(() => {
        setShowLoader(true);
        merchants.paymentsAccess()
            .then((resp) => {
                resp && setData(resp);
                setShowLoader(false);
            })
            .catch(() => setShowLoader(false));
    }, [setShowLoader]);

    const rows = useMemo(() => {
        const list = Array.isArray(data?.merchants) ? [...data.merchants] : [];
        const filtered = view === 'on'
            ? list.filter(hasPaymentsAccess)
            : view === 'off'
                ? list.filter((row) => !hasPaymentsAccess(row))
                : list;
        return filtered.sort((a, b) => {
            const aOn = hasPaymentsAccess(a) ? 1 : 0;
            const bOn = hasPaymentsAccess(b) ? 1 : 0;
            if (aOn !== bOn) return bOn - aOn;
            const aLimit = a.max_daily_volume != null ? parseFloat(a.max_daily_volume) : 0;
            const bLimit = b.max_daily_volume != null ? parseFloat(b.max_daily_volume) : 0;
            return (Number.isNaN(bLimit) ? 0 : bLimit) - (Number.isNaN(aLimit) ? 0 : aLimit);
        });
    }, [data, view]);

    const enabledLimitsTotal = useMemo(() => {
        const list = Array.isArray(data?.merchants) ? data.merchants : [];
        return list.reduce((sum, row) => {
            if (!hasPaymentsAccess(row)) return sum;
            const v = parseDailyLimit(row);
            return v == null ? sum : sum + v;
        }, 0);
    }, [data]);

    const enabledTodayTotal = useMemo(() => {
        if (typeof data?.today_total === 'number') return data.today_total;
        const list = Array.isArray(data?.merchants) ? data.merchants : [];
        return list.reduce((sum, row) => {
            if (!hasPaymentsAccess(row)) return sum;
            const v = parseFloat(row.day_total || 0);
            return Number.isNaN(v) ? sum : sum + v;
        }, 0);
    }, [data]);

    if (!data) return null;

    return (
        <React.Fragment>
            <TableToolbar
                title="Merchant payment access"
                actions={
                    <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Typography variant="body2" color="text.secondary">
                            {data.with_access} of {data.total} have payments on
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 800 }}>
                            Total daily limits: {formatAmount(enabledLimitsTotal, currencySymbol)}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 800 }}>
                            Today: {formatAmount(enabledTodayTotal, currencySymbol)}
                        </Typography>
                        <ToggleButtonGroup
                            value={view}
                            size="small"
                            exclusive
                            onChange={(event, value) => value && setView(value)}
                            aria-label="Merchant payments filter"
                        >
                            <ToggleButton value="on">Payments on</ToggleButton>
                            <ToggleButton value="off">Payments off</ToggleButton>
                            <ToggleButton value="all">All</ToggleButton>
                        </ToggleButtonGroup>
                    </Stack>
                }
            />
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>ID</TableCell>
                        <TableCell>Username</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Enabled</TableCell>
                        <TableCell>Payments</TableCell>
                        <TableCell>Daily limit</TableCell>
                        <TableCell>Today</TableCell>
                        <TableCell>Remaining</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((row) => (
                        <TableRow key={`mpa-${row.id}`}>
                            <TableCell>{row.id}</TableCell>
                            <TableCell>
                                <Link to={`/merchants/edit?uuid=${helpers.UUIDv4Transform(row.uuid)}`} component={RouterLink}>
                                    {row.username}
                                </Link>
                            </TableCell>
                            <TableCell>{row.name}</TableCell>
                            <TableCell>{row.enabled ? <Check fontSize="small" /> : ''}</TableCell>
                            <TableCell>
                                <Chip
                                    size="small"
                                    label={hasPaymentsAccess(row) ? 'On' : 'Off'}
                                    color={hasPaymentsAccess(row) ? 'success' : 'default'}
                                    variant={hasPaymentsAccess(row) ? 'filled' : 'outlined'}
                                />
                            </TableCell>
                            <TableCell>{formatDailyLimit(row, currencySymbol)}</TableCell>
                            <TableCell>{formatAmount(parseFloat(row.day_total || 0) || 0, currencySymbol)}</TableCell>
                            <TableCell>{formatRemaining(row, currencySymbol)}</TableCell>
                        </TableRow>
                    ))}
                    {rows.length === 0 && (
                        <TableRow>
                            <TableCell colSpan={8}>
                                <Typography variant="body2" color="text.secondary">No merchants in this filter.</Typography>
                            </TableCell>
                        </TableRow>
                    )}
                    <TableRow>
                        <TableCell colSpan={5} sx={{ fontWeight: 800 }}>
                            Total (payments on)
                        </TableCell>
                        <TableCell sx={{ fontWeight: 800 }}>
                            {formatAmount(enabledLimitsTotal, currencySymbol)}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 800 }}>
                            {formatAmount(enabledTodayTotal, currencySymbol)}
                        </TableCell>
                        <TableCell />
                    </TableRow>
                </TableBody>
            </Table>
        </React.Fragment>
    );
}
